import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Plus, Trash2, Banknote, Search, ChevronDown, ChevronRight, Wallet, Pencil,
  Landmark, IndianRupee,
} from "lucide-react";
import { inr, fmtDate } from "@/utils";
import { toast } from "sonner";
import { usePagination } from "@/components/Pagination";

const LENDER_TYPES = [
  { v: "bank", l: "Bank" },
  { v: "private", l: "Private" },
  { v: "nbfc", l: "NBFC" },
];
const LOAN_TYPES = [
  { v: "construction", l: "Construction" },
  { v: "working_capital", l: "Working Capital" },
  { v: "project", l: "Project Finance" },
  { v: "personal", l: "Personal" },
  { v: "other", l: "Other" },
];
const PAY_MODES = [
  { v: "bank_transfer", l: "Bank Transfer" },
  { v: "cheque", l: "Cheque" },
  { v: "cash", l: "Cash" },
  { v: "upi", l: "UPI" },
  { v: "rtgs", l: "RTGS/NEFT" },
];
const STATUSES = [
  { v: "active", l: "Active" },
  { v: "closed", l: "Closed" },
];

const EMPTY_LOAN = {
  lender_name: "", lender_type: "bank", loan_type: "construction",
  account_number: "", project_id: "",
  principal: 0, interest_rate: 0, tenure_months: 0, emi: 0,
  disbursement_date: new Date().toISOString().slice(0, 10),
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "", notes: "", status: "active",
};

function computeEMI(principal, ratePct, tenureMonths) {
  const P = Number(principal) || 0;
  const N = Number(tenureMonths) || 0;
  const r = (Number(ratePct) || 0) / 12 / 100;
  if (P <= 0 || N <= 0) return 0;
  if (r === 0) return +(P / N).toFixed(2);
  const pow = Math.pow(1 + r, N);
  return +((P * r * pow) / (pow - 1)).toFixed(2);
}

export default function Loans() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/projects")).data,
  });
  const [projectFilter, setProjectFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const { data: loans = [] } = useQuery({
    queryKey: ["loans", projectFilter, statusFilter],
    queryFn: async () => {
      const params = {};
      if (projectFilter) params.project_id = projectFilter;
      if (statusFilter) params.status = statusFilter;
      return (await api.get("/loans", { params })).data;
    },
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return loans;
    const q = search.toLowerCase();
    return loans.filter((l) =>
      (l.lender_name || "").toLowerCase().includes(q) ||
      (l.loan_number || "").toLowerCase().includes(q) ||
      (l.account_number || "").toLowerCase().includes(q) ||
      (l.loan_type || "").toLowerCase().includes(q)
    );
  }, [loans, search]);

  const { pageRows, controls } = usePagination(filtered);

  const totals = useMemo(() => {
    let principal = 0, outstanding = 0, paid = 0, interest = 0, active = 0;
    for (const l of loans) {
      principal += Number(l.principal || 0);
      outstanding += Number(l.summary?.outstanding || 0);
      paid += Number(l.summary?.total_paid || 0);
      interest += Number(l.summary?.interest_paid || 0);
      if (l.status === "active") active += 1;
    }
    return { principal, outstanding, paid, interest, active, count: loans.length };
  }, [loans]);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_LOAN);

  const autoEmi = useMemo(
    () => computeEMI(form.principal, form.interest_rate, form.tenure_months),
    [form.principal, form.interest_rate, form.tenure_months]
  );

  function openNew() {
    setEditingId(null);
    setForm(EMPTY_LOAN);
    setOpen(true);
  }

  function openEdit(l) {
    setEditingId(l.id);
    setForm({
      ...EMPTY_LOAN,
      ...l,
      project_id: l.project_id || "",
      account_number: l.account_number || "",
      notes: l.notes || "",
      end_date: (l.end_date || "").slice(0, 10),
      disbursement_date: (l.disbursement_date || "").slice(0, 10),
      start_date: (l.start_date || "").slice(0, 10),
    });
    setOpen(true);
  }

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["loans"] });
  }

  async function save() {
    if (!form.lender_name.trim()) return toast.error("Lender name required");
    if (!(+form.principal > 0)) return toast.error("Principal must be greater than 0");
    try {
      const payload = {
        ...form,
        project_id: form.project_id || null,
        principal: +form.principal || 0,
        interest_rate: +form.interest_rate || 0,
        tenure_months: +form.tenure_months || 0,
        emi: +form.emi || autoEmi,
        end_date: form.end_date || null,
        account_number: form.account_number || null,
        notes: form.notes || null,
      };
      if (editingId) {
        await api.patch(`/loans/${editingId}`, payload);
        toast.success("Loan updated");
      } else {
        await api.post("/loans", payload);
        toast.success("Loan added");
      }
      invalidate();
      setOpen(false);
      setForm(EMPTY_LOAN);
      setEditingId(null);
    } catch (e) {
      toast.error(apiErr(e, "Failed to save loan"));
    }
  }

  async function del(l) {
    if (!window.confirm(`Delete loan ${l.loan_number} from ${l.lender_name}?\n\nIf repayments exist, deletion will be blocked — mark as Closed instead.`)) return;
    try {
      await api.delete(`/loans/${l.id}`);
      toast.success("Loan deleted");
      invalidate();
    } catch (e) {
      const detail = apiErr(e);
      if (detail.toLowerCase().includes("force=true")) {
        if (window.confirm(`${detail}\n\nForce delete loan AND all repayments?`)) {
          try {
            await api.delete(`/loans/${l.id}?force=true`);
            toast.success("Loan and repayments deleted");
            invalidate();
          } catch (er) { toast.error(apiErr(er)); }
        }
      } else { toast.error(detail); }
    }
  }

  return (
    <div className="space-y-6" data-testid="loans-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Finance</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Loans &amp; Repayments</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Track every bank / private / NBFC loan taken by the company. Record EMIs and repayments; outstanding balance recalculates automatically.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              placeholder="Search lender, loan #, account…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-64 pl-8"
              data-testid="loan-search"
            />
          </div>
          <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="loan-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={projectFilter || "__all__"} onValueChange={(v) => setProjectFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="loan-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openNew} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-loan-btn">
            <Plus size={14} className="mr-1" /> Add Loan
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Loans</div>
          <div className="text-xl font-bold font-mono mt-1">{totals.count} <span className="text-xs text-zinc-400">({totals.active} active)</span></div>
        </CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Principal Sanctioned</div>
          <div className="text-lg font-bold font-mono mt-1">{inr(totals.principal)}</div>
        </CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Repaid</div>
          <div className="text-lg font-bold font-mono mt-1 text-emerald-700">{inr(totals.paid)}</div>
        </CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Interest Paid</div>
          <div className="text-lg font-bold font-mono mt-1 text-amber-700">{inr(totals.interest)}</div>
        </CardContent></Card>
        <Card className="bg-white border-zinc-200"><CardContent className="p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Outstanding</div>
          <div className={`text-lg font-bold font-mono mt-1 ${totals.outstanding > 0 ? "text-red-600" : "text-emerald-700"}`}>{inr(totals.outstanding)}</div>
        </CardContent></Card>
      </div>

      {/* Loan dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY_LOAN); setEditingId(null); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Edit Loan" : "New Loan"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="col-span-2 md:col-span-1">
              <Label>Lender Name *</Label>
              <Input value={form.lender_name} onChange={(e) => setForm({ ...form, lender_name: e.target.value })} placeholder="HDFC Bank / Ramesh Uncle / …" data-testid="loan-lender-name" />
            </div>
            <div>
              <Label>Lender Type</Label>
              <Select value={form.lender_type} onValueChange={(v) => setForm({ ...form, lender_type: v })}>
                <SelectTrigger data-testid="loan-lender-type"><SelectValue /></SelectTrigger>
                <SelectContent>{LENDER_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Loan Type</Label>
              <Select value={form.loan_type} onValueChange={(v) => setForm({ ...form, loan_type: v })}>
                <SelectTrigger data-testid="loan-type"><SelectValue /></SelectTrigger>
                <SelectContent>{LOAN_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Loan / Account Number</Label>
              <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="e.g., LN1234567" />
            </div>
            <div>
              <Label>Project (optional)</Label>
              <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Company-level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Company-level —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Principal (₹) *</Label>
              <Input type="number" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} data-testid="loan-principal" />
            </div>
            <div>
              <Label>Interest Rate (% p.a.)</Label>
              <Input type="number" step="0.01" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} data-testid="loan-rate" />
            </div>
            <div>
              <Label>Tenure (months)</Label>
              <Input type="number" value={form.tenure_months} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} data-testid="loan-tenure" />
            </div>
            <div>
              <Label>EMI (₹) <span className="text-[10px] text-emerald-700 font-normal ml-1">auto: {inr(autoEmi)}</span></Label>
              <div className="flex gap-1">
                <Input type="number" value={form.emi} onChange={(e) => setForm({ ...form, emi: e.target.value })} data-testid="loan-emi" />
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, emi: autoEmi })} className="whitespace-nowrap">Use Auto</Button>
              </div>
            </div>
            <div>
              <Label>Disbursement Date</Label>
              <Input type="date" value={form.disbursement_date} onChange={(e) => setForm({ ...form, disbursement_date: e.target.value })} />
            </div>
            <div>
              <Label>EMI Start Date</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <Label>End Date (optional)</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div className="col-span-2 md:col-span-3">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Purpose, guarantor, security etc." />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-loan-btn">
              {editingId ? "Save changes" : "Add loan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loans table */}
      <Card className="bg-white border-zinc-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-zinc-50"><TableRow>
              <TableHead className="w-8" />
              <TableHead>Loan #</TableHead>
              <TableHead>Lender</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Principal</TableHead>
              <TableHead className="text-right">EMI · Rate</TableHead>
              <TableHead className="text-right">Repaid</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pageRows.map((l) => {
                const isExpanded = expandedId === l.id;
                const s = l.summary || {};
                return (
                  <React.Fragment key={l.id}>
                    <TableRow className="data-row" data-testid={`loan-row-${l.loan_number}`}>
                      <TableCell className="pr-0">
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : l.id)}
                          className="p-1 rounded hover:bg-zinc-100 text-zinc-600"
                          title={isExpanded ? "Hide repayments" : "Show repayments"}
                          data-testid={`toggle-repayments-${l.loan_number}`}
                        >
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </TableCell>
                      <TableCell className="font-mono text-xs font-semibold">{l.loan_number}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Landmark size={12} className="text-zinc-500 shrink-0" />
                          <div>
                            <div className="text-sm font-medium">{l.lender_name}</div>
                            <div className="text-[10px] text-zinc-500">
                              {LENDER_TYPES.find((t) => t.v === l.lender_type)?.l || l.lender_type}
                              {l.account_number ? ` · ${l.account_number}` : ""}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <span className="uppercase tracking-widest text-[10px] bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 rounded">
                          {LOAN_TYPES.find((t) => t.v === l.loan_type)?.l || l.loan_type}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">{inr(l.principal)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        <div>{inr(l.emi)}</div>
                        <div className="text-[10px] text-zinc-500">{l.interest_rate}% · {l.tenure_months}m</div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{inr(s.total_paid || 0)}</TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${(s.outstanding || 0) > 0 ? "text-red-600" : "text-emerald-700"}`}>
                        {inr(s.outstanding || 0)}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${l.status === "active" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-zinc-100 border-zinc-200 text-zinc-600"}`}>
                          {l.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          size="sm"
                          className="mr-1 bg-emerald-700 hover:bg-emerald-800 text-white h-8 px-2"
                          onClick={() => setExpandedId(l.id)}
                          data-testid={`add-repayment-btn-${l.loan_number}`}
                          title="Record a repayment"
                        >
                          <Wallet size={12} className="mr-1" /> Repayment
                        </Button>
                        <Button size="sm" variant="outline" className="mr-1" onClick={() => openEdit(l)} data-testid={`edit-loan-${l.loan_number}`}>
                          <Pencil size={12} />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => del(l)} data-testid={`delete-loan-${l.loan_number}`}>
                          <Trash2 size={12} />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className="bg-zinc-50/60 hover:bg-zinc-50/60">
                        <TableCell colSpan={10} className="p-0">
                          <LoanRepayments loan={l} onChange={invalidate} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {!filtered.length && (
                <TableRow><TableCell colSpan={10} className="text-center text-sm text-zinc-500 py-14">
                  No loans yet. Click <b>Add Loan</b> to record the first bank / private loan.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {controls}
        </CardContent>
      </Card>
    </div>
  );
}


// ============ Loan Repayments (inline ledger) ============
function LoanRepayments({ loan, onChange }) {
  const qc = useQueryClient();
  const { data: repayments = [] } = useQuery({
    queryKey: ["loan-repayments", loan.id],
    queryFn: async () => (await api.get("/loan-repayments", { params: { loan_id: loan.id } })).data,
    enabled: !!loan.id,
  });

  const [form, setForm] = useState({
    amount: "", principal_component: "", interest_component: "",
    date: new Date().toISOString().slice(0, 10),
    payment_mode: "bank_transfer", reference_number: "", notes: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const paid = repayments.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const principalPaid = repayments.reduce((s, r) => s + (Number(r.principal_component) || 0), 0);
  const interestPaid = repayments.reduce((s, r) => s + (Number(r.interest_component) || 0), 0);
  const outstanding = Math.max((Number(loan.principal) || 0) - principalPaid, 0);

  function resetForm() {
    setForm({
      amount: "", principal_component: "", interest_component: "",
      date: new Date().toISOString().slice(0, 10),
      payment_mode: "bank_transfer", reference_number: "", notes: "",
    });
    setEditingId(null);
  }

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["loan-repayments", loan.id] });
    qc.invalidateQueries({ queryKey: ["loans"] });
    onChange?.();
  }

  function payFullEMI() {
    const emi = Number(loan.emi) || 0;
    if (emi <= 0) return toast.error("EMI is not set on this loan");
    setForm({ ...form, amount: emi.toFixed(2) });
  }

  function payOutstanding() {
    if (outstanding <= 0) return toast.info("Nothing outstanding");
    setForm({ ...form, amount: outstanding.toFixed(2), principal_component: outstanding.toFixed(2), interest_component: "" });
  }

  async function save() {
    const amt = Number(form.amount) || 0;
    if (amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      const payload = {
        loan_id: loan.id,
        amount: amt,
        principal_component: +form.principal_component || 0,
        interest_component: +form.interest_component || 0,
        date: form.date,
        payment_mode: form.payment_mode,
        reference_number: form.reference_number || null,
        notes: form.notes || null,
      };
      if (editingId) {
        await api.patch(`/loan-repayments/${editingId}`, payload);
        toast.success("Repayment updated");
      } else {
        await api.post("/loan-repayments", payload);
        toast.success(`Repayment ${inr(amt)} recorded`);
      }
      resetForm();
      invalidate();
    } catch (e) {
      toast.error(apiErr(e, "Failed to save repayment"));
    } finally { setBusy(false); }
  }

  function openEditRow(r) {
    setEditingId(r.id);
    setForm({
      amount: r.amount || "",
      principal_component: r.principal_component || "",
      interest_component: r.interest_component || "",
      date: (r.date || "").slice(0, 10),
      payment_mode: r.payment_mode || "bank_transfer",
      reference_number: r.reference_number || "",
      notes: r.notes || "",
    });
  }

  async function del(id) {
    if (!window.confirm("Delete this repayment?")) return;
    try {
      await api.delete(`/loan-repayments/${id}`);
      toast.success("Repayment deleted");
      if (editingId === id) resetForm();
      invalidate();
    } catch (e) { toast.error(apiErr(e)); }
  }

  return (
    <div className="px-4 py-3 border-t border-zinc-200" data-testid={`repayments-panel-${loan.loan_number}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold">
          <Wallet size={13} /> Repayment Ledger
          <span className="font-mono normal-case text-zinc-500">· {repayments.length} entr{repayments.length === 1 ? "y" : "ies"}</span>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono flex-wrap">
          <span>Principal <b>{inr(loan.principal)}</b></span>
          <span className="text-emerald-700">Paid <b>{inr(paid)}</b></span>
          <span className="text-amber-700">Interest <b>{inr(interestPaid)}</b></span>
          <span className={outstanding > 0 ? "text-red-600" : "text-zinc-400"}>
            Outstanding <b>{inr(outstanding)}</b>
          </span>
        </div>
      </div>

      {/* Repayments list */}
      <div className="rounded border border-zinc-200 overflow-hidden mb-3 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50">
            <tr className="text-left text-zinc-500 uppercase tracking-widest text-[10px]">
              <th className="px-3 py-1.5">Date</th>
              <th className="px-3 py-1.5">Mode</th>
              <th className="px-3 py-1.5">Reference</th>
              <th className="px-3 py-1.5 text-right">Principal</th>
              <th className="px-3 py-1.5 text-right">Interest</th>
              <th className="px-3 py-1.5 text-right">Amount</th>
              <th className="px-3 py-1.5 text-right w-[100px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {repayments.map((r) => (
              <tr key={r.id} className={`border-t border-zinc-100 ${editingId === r.id ? "bg-emerald-50" : ""}`} data-testid={`repayment-row-${r.id}`}>
                <td className="px-3 py-1.5 font-mono">{fmtDate(r.date)}</td>
                <td className="px-3 py-1.5">{(r.payment_mode || "").replace("_", " ")}</td>
                <td className="px-3 py-1.5 font-mono text-zinc-500">{r.reference_number || "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono">{inr(r.principal_component || 0)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-amber-700">{inr(r.interest_component || 0)}</td>
                <td className="px-3 py-1.5 text-right font-mono text-emerald-700 font-semibold">{inr(r.amount)}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button
                    onClick={() => openEditRow(r)}
                    className="text-zinc-600 hover:text-zinc-900 p-1"
                    title="Edit repayment"
                    data-testid={`edit-repayment-${r.id}`}
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => del(r.id)}
                    className="text-red-500 hover:text-red-700 p-1 ml-1"
                    title="Delete repayment"
                    data-testid={`del-repayment-${r.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
            {!repayments.length && (
              <tr><td colSpan={7} className="px-3 py-3 text-center text-zinc-400 italic">No repayments recorded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit repayment form */}
      <div className="bg-white border border-emerald-200 rounded p-2">
        <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
            {editingId ? "Edit Repayment" : "Record Repayment"}
          </div>
          <div className="text-[10px] text-zinc-500 flex-1 text-center min-w-[200px]" data-testid={`pl-hint-${loan.loan_number}`}>
            {loan.project_id ? (
              <>Interest auto-books to <b>P&amp;L</b> as <b>Loan Interest</b>. Principal is balance-only.</>
            ) : (
              <>Assign a project to this loan to auto-book interest into P&amp;L.</>
            )}
          </div>
          <div className="flex gap-1">
            <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={payFullEMI} data-testid={`pay-emi-${loan.loan_number}`}>Use EMI</Button>
            {outstanding > 0 && (
              <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={payOutstanding} data-testid={`pay-outstanding-${loan.loan_number}`}>Full O/S</Button>
            )}
            {editingId && (
              <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={resetForm}>Cancel edit</Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Amount ₹</Label>
            <div className="relative">
              <IndianRupee size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="h-8 pl-6 text-xs"
                placeholder="0"
                data-testid={`repay-amount-${loan.loan_number}`}
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Principal ₹</Label>
            <Input type="number" value={form.principal_component} onChange={(e) => setForm({ ...form, principal_component: e.target.value })} className="h-8 text-xs" placeholder="Optional" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Interest ₹</Label>
            <Input type="number" value={form.interest_component} onChange={(e) => setForm({ ...form, interest_component: e.target.value })} className="h-8 text-xs" placeholder="Optional" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-8 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Mode</Label>
            <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`repay-mode-${loan.loan_number}`}><SelectValue /></SelectTrigger>
              <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Reference #</Label>
            <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} className="h-8 text-xs" placeholder="Cheque / UTR" />
          </div>
          <div>
            <Button
              type="button"
              size="sm"
              className="h-8 w-full bg-emerald-700 hover:bg-emerald-800 text-white"
              disabled={busy}
              onClick={save}
              data-testid={`save-repayment-${loan.loan_number}`}
            >
              <Plus size={12} className="mr-1" /> {busy ? "…" : (editingId ? "Save" : "Record")}
            </Button>
          </div>
          <div className="md:col-span-7">
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-8 text-xs" placeholder="Notes (optional)" />
          </div>
        </div>
      </div>
    </div>
  );
}
